import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

interface PageSelectionProps {
  totalPages: number;
  onPagesSelect: (selectedPages: number[], totalCost: number, copies: number) => void;
}

export const PageSelection: React.FC<PageSelectionProps> = ({ totalPages, onPagesSelect }) => {
  const [pageInput, setPageInput] = useState<string>('');
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [error, setError] = useState<string>('');
  const [copies, setCopies] = useState<number>(1);
  const costPerPage = 2;

  const parsePageSelection = (input: string): number[] => {
    if (!input.trim()) return [];
    
    const pages: number[] = [];
    const parts = input.split(',').map((part: string) => part.trim());
    
    for (const part of parts) {
      const trimmed = part.trim();
      
      if (trimmed.includes('-')) {
        // Range like "1-5"
        const [start, end] = trimmed.split('-').map((num: string) => parseInt(num.trim()));
        if (isNaN(start) || isNaN(end) || start > end || start < 1 || end > totalPages) {
          throw new Error(`Invalid range: ${trimmed}`);
        }
        for (let i = start; i <= end; i++) {
          if (!pages.includes(i)) pages.push(i);
        }
      } else {
        // Single page
        const page = parseInt(trimmed);
        if (isNaN(page) || page < 1 || page > totalPages) {
          throw new Error(`Invalid page: ${trimmed}`);
        }
        if (!pages.includes(page)) pages.push(page);
      }
    }
    
    return pages.sort((a, b) => a - b);
  };

  const handleInputChange = (value: string) => {
    setPageInput(value);
    setError('');
    
    try {
      const pages = parsePageSelection(value);
      setSelectedPages(pages);
      const totalCost = pages.length * costPerPage * copies;
      onPagesSelect(pages, totalCost, copies);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid page selection');
      setSelectedPages([]);
      onPagesSelect([], 0, copies);
    }
  };

  const handleCopiesChange = (newCopies: number) => {
    if (newCopies < 1) return;
    setCopies(newCopies);
    if (selectedPages.length > 0) {
      const totalCost = selectedPages.length * costPerPage * newCopies;
      onPagesSelect(selectedPages, totalCost, newCopies);
    }
  };

  const handleSelectAll = () => {
    const allPages = Array.from({ length: totalPages }, (_, i) => i + 1);
    setPageInput(`1-${totalPages}`);
    setSelectedPages(allPages);
    const totalCost = allPages.length * costPerPage * copies;
    onPagesSelect(allPages, totalCost, copies);
    setError('');
  };

  return (
    <div style={{
      animation: 'float 6s ease-in-out infinite, fadeIn 0.5s ease',
      width: '100%'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: '2rem',
        boxShadow: '0 20px 40px -10px rgba(0,0,0,0.1), 0 10px 20px -5px rgba(0,0,0,0.04)',
        border: '1px solid rgba(226, 232, 240, 0.8)',
        position: 'relative',
        zIndex: 10
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '80px', height: '80px', background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1rem', boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.3)',
            animation: 'pulse 2s infinite'
          }}>
            <Calculator style={{ width: '40px', height: '40px', color: '#2563eb' }} />
          </div>
          <p style={{ color: '#64748b', fontSize: '15px', fontWeight: '500', margin: 0 }}>
            Total pages detected: <span style={{ fontWeight: '800', color: '#0f172a', fontSize: '24px', marginLeft: '8px' }}>{totalPages}</span>
          </p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div>
            <label htmlFor="page-input" style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '8px', display: 'block', textTransform: 'uppercase', letterSpacing: '1px' }}>
              Select Pages
            </label>
            <input
              id="page-input"
              placeholder="e.g., 1-3,5,7 or 1,2,3"
              value={pageInput}
              onChange={(e) => handleInputChange(e.target.value)}
              style={{
                width: '100%', height: '56px', borderRadius: '12px', padding: '0 16px', fontSize: '16px',
                border: error ? '2px solid #ef4444' : '2px solid #e2e8f0',
                outline: 'none', transition: 'all 0.2s', backgroundColor: '#f8fafc',
                boxSizing: 'border-box', color: '#0f172a', fontWeight: '500'
              }}
              onFocus={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.1)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = error ? '#ef4444' : '#e2e8f0'; e.currentTarget.style.backgroundColor = '#f8fafc'; e.currentTarget.style.boxShadow = 'none'; }}
            />
            {error && (
              <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px', fontWeight: '600', animation: 'fadeIn 0.3s' }}>{error}</p>
            )}
          </div>

          <button 
            onClick={handleSelectAll}
            style={{
              width: '100%', height: '56px', borderRadius: '12px', background: 'transparent',
              color: '#3b82f6', border: '2px solid #bfdbfe', fontWeight: '700', fontSize: '16px',
              cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
            onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(59,130,246,0.1)'; }}
            onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            Select All Pages
          </button>

          {selectedPages.length > 0 && (
            <div style={{
              background: 'linear-gradient(to right bottom, #eff6ff, #f8fafc)', border: '1px solid #bfdbfe',
              padding: '1.5rem', borderRadius: '16px', animation: 'fadeIn 0.4s ease',
              boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.1)'
            }}>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#475569', marginBottom: '8px', display: 'block', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  Number of Copies
                </label>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '1rem', background: 'white',
                  padding: '6px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                  border: '1px solid #e2e8f0'
                }}>
                  <button 
                    onClick={() => handleCopiesChange(copies - 1)}
                    disabled={copies <= 1}
                    style={{
                      width: '40px', height: '40px', borderRadius: '8px', border: 'none',
                      background: copies <= 1 ? '#f1f5f9' : '#eff6ff', color: copies <= 1 ? '#94a3b8' : '#2563eb',
                      fontSize: '20px', fontWeight: 'bold', cursor: copies <= 1 ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => { if (copies > 1) { e.currentTarget.style.background = '#dbeafe'; } }}
                    onMouseOut={(e) => { if (copies > 1) { e.currentTarget.style.background = '#eff6ff'; } }}
                  >-</button>
                  <span style={{ fontWeight: '800', width: '40px', textAlign: 'center', fontSize: '20px', color: '#0f172a' }}>{copies}</span>
                  <button 
                    onClick={() => handleCopiesChange(copies + 1)}
                    style={{
                      width: '40px', height: '40px', borderRadius: '8px', border: 'none',
                      background: '#eff6ff', color: '#2563eb',
                      fontSize: '20px', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#dbeafe'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#eff6ff'}
                  >+</button>
                </div>
              </div>
              
              <div style={{ paddingTop: '1rem', borderTop: '2px dashed #bfdbfe' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Selected</span>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{selectedPages.length} pages</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase' }}>Total Cost</span>
                  <span style={{ fontSize: '28px', fontWeight: '800', color: '#2563eb' }}>₹{selectedPages.length * costPerPage * copies}</span>
                </div>
              </div>
            </div>
          )}

          <div style={{
            fontSize: '13px', fontWeight: '500', color: '#64748b', background: '#f8fafc',
            padding: '1rem', borderRadius: '12px', border: '1px solid #f1f5f9', lineHeight: '1.6'
          }}>
            <span style={{ color: '#475569', fontWeight: '700', marginBottom: '4px', display: 'block' }}>💡 Quick Tips:</span>
            Use "1-5" to print pages 1 to 5.<br/>
            Use "1,3,5" to print specific pages.
          </div>
        </div>
      </div>
      
      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-8px); }
            100% { transform: translateY(0px); }
          }
          @keyframes fadeIn { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes pulse { 0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); } 70% { transform: scale(1.05); box-shadow: 0 0 0 10px rgba(59, 130, 246, 0); } 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); } }
        `}
      </style>
    </div>
  );
};
